import { computed, toValue } from "vue";
import Tinycolor from "tinycolor2";
import type { MaybeRefOrGetter } from "vue";
import { useColorMode, useDark } from "@vueuse/core";
import { defineStore } from "pinia";

export function useResolvedColor(colorString: MaybeRefOrGetter<string>) {
  return computed(() => {
    const colorStringValue = toValue(colorString);
    if (colorStringValue.includes("var(--")) {
      const colorVarString = colorStringValue
        .replace("var(", "")
        .replace(")", "");
      return window.getComputedStyle(document.documentElement).getPropertyValue(colorVarString);
    }
    return colorStringValue;
  });
}

export function useBrightness(colorString: MaybeRefOrGetter<string>) {
  return computed(() => Tinycolor(toValue(colorString)).getBrightness() / 255);
}

export function getContrastColor(backgroundColor: string) {
  if (backgroundColor == "transparent" || backgroundColor == "rgba(0,0,0,0)") {
    return "var(--color-text)";
  }
  return Tinycolor.mostReadable(backgroundColor, ["#fff", "#000"], {
    includeFallbackColors: true,
    level: "AA",
    size: "large"
  }).toRgbString();
}

export function useContrastColor(backgroundColor: MaybeRefOrGetter<string | null | undefined>) {
  return computed(() => getContrastColor(toValue(backgroundColor) || "transparent"));
}

/**
 * 
 * @param colorString A color string.
 * @param amount The amount to darken the color by (0-100).
 * @returns 
 */
export function darken(colorString: MaybeRefOrGetter<string>, amount: number) {
  const color = Tinycolor(toValue(colorString));
  return color.isValid() ? color.darken(amount).toHslString() : "transparent";
}

export function lighten(colorString: MaybeRefOrGetter<string>, amount: number) {
  const color = Tinycolor(toValue(colorString));
  return color.isValid() ? color.lighten(amount).toHslString() : "transparent";
}

export function toHSLA(colorString: MaybeRefOrGetter<string>): {
  h: number;
  s: number;
  l: number;
  a: number;
} {
  const color = Tinycolor(toValue(colorString));
  if (!color.isValid()) {
    return { h: 0, s: 0, l: 0, a: 1 }; // Return a default value for invalid colors
  }
  const hsla = color.toHsl();
  return {
    h: hsla.h,
    s: hsla.s,
    l: hsla.l,
    a: hsla.a
  };
}

export function setOpacity(colorString: MaybeRefOrGetter<string>, opacity: number) {
  const color = Tinycolor(toValue(colorString));
  if (color.isValid()) {
    return color.setAlpha(opacity).toHslString();
  }
  return "transparent";
}

const isDark = useDark();

export const contrastLightness = computed(() => {
  return isDark.value ? 100 : 0;
});

export const useColor = defineStore("ui/color", () => {
  const { system: colorModeSystem, store: colorModeStore } = useColorMode();
  
  const colorMode = computed(() => {
    return colorModeStore.value === 'auto' ? colorModeSystem.value : colorModeStore.value;
  });

  function setDark(value: boolean | null) {
    if (value === null) {
      colorModeStore.value = 'auto';
    } else if (value === true) {
      colorModeStore.value = 'dark';
    } else {
      colorModeStore.value = 'light';
    }
  }

  return {
    colorMode,
    setDark
  };
});
